import { Component as VueComponent, defineComponent, h } from 'vue';

import noop from 'lodash/noop';

import IdleTimeTracker from '@evidentid/idle-time-tracker';
import { RelyingPartyRequestStatusType } from '@evidentid/ido-lib/getIdoRequestStatus';
import delay from '@evidentid/universal-framework/delay';

import * as config from '@/config/interviewUpdates';
import { Status } from '@/store/interfaces/IdoState';
import { useIdoStore } from '@/store/store';

const IGNORED_STATUSES = [ Status.error, Status.uninitialized, Status.loading, Status.loggedOut ];
const FINAL_REQUEST_STATUSES = [
    RelyingPartyRequestStatusType.complete,
    RelyingPartyRequestStatusType.canceled,
    RelyingPartyRequestStatusType.timeout,
    RelyingPartyRequestStatusType.maxAttemptsExceeded,
];

const easeOutQuad = (t: number) => t * (2 - t);

export default function withInterviewUpdates(
    Component: VueComponent,
    blockImmediateFinish = false,

    now = Date.now,
) {
    // PRODUCT-26110 Phase 2B2 Task 5 batch 4: see withErrorHandler.ts's identical note --
    // `Vue.component(name, ...)` used only as a definition-object builder, never
    // registered/looked-up by name; `defineComponent` is the direct Vue 3 replacement.
    return defineComponent({
        data: () => ({
            isMounted: false,
            tracker: new IdleTimeTracker({ now }),
        }),

        mounted() {
            this.isMounted = true;
            this.tracker = new IdleTimeTracker({ now });
            this.tracker.start();
            this.tick();
        },

        unmounted() {
            this.isMounted = false;
            this.tracker?.stop();
        },

        methods: {
            // A hand-off leaves the desktop with no input for as long as the capture takes,
            // so the ordinary idle limit would stop polling while the feature is working.
            inactivityLimit(): number {
                const delegations = Object.values(useIdoStore().delegationsRequested);
                const awaitingHandoff = delegations.some((d) => d.status === Status.success);

                return awaitingHandoff ? config.maxHandoffInactivityTime : config.maxInactivityTime;
            },

            async tick() {
                // Calculate user idleness time
                const idleTime = this.tracker.get();

                // Stop when it is not mounted
                if (!this.isMounted) {
                    return;
                }

                // When user was inactive too long, reschedule update.
                if (idleTime > this.inactivityLimit()) {
                    await delay(500);
                    this.tick();
                    return;
                }

                // Wait selected time before next update
                const maxDelayDiff = config.maxRefreshDelayTime - config.minRefreshDelayTime;
                const t = easeOutQuad(Math.min(idleTime / config.maxInactivityTime, 1));
                const delayTime = config.minRefreshDelayTime + maxDelayDiff * t;
                await delay(delayTime);

                // Schedule next update
                if (this.isMounted) {
                    await this.updateInterview();
                    this.tick();
                }
            },

            async updateInterview() {
                // Load required data
                const { status, requestStatus } = useIdoStore();

                // Do not update when interview is not started, loading or failed.
                if (IGNORED_STATUSES.includes(status)) {
                    return;
                }

                // Do not update when the interview can't change anymore.
                if (requestStatus && FINAL_REQUEST_STATUSES.includes(requestStatus.type)) {
                    return;
                }

                // Ignore reloading when
                if (!useIdoStore().featureFlags?.rpr_constant_polling_enabled) {
                    return;
                }

                // Initialize interview refresh
                return useIdoStore().reloadInterview({ blockImmediateFinish }).catch(noop);
            },
        },

        render() {
            return h(Component);
        },
    });
}
